import { useEffect, useRef, useState } from "react";
import { findSpots, spotlight } from "@/help/spotlight";
import { Link } from "react-router-dom";
import type { HelpTopic } from "@/help/content";

/**
 * 화면 도움말 오른쪽 드로어.
 *
 * 헤더의 `? 도움말` 을 누르면 현재 경로에 해당하는 가이드를 연다.
 * 담는 것은 네 가지다 — 화면 캡처, 이 화면에서 하는 일, 화면에 실제로 있는 버튼 이름과 설명,
 * 먼저 채워 둬야 하는 기준정보 화면 링크.
 *
 * 디자인 원칙(CLAUDE.md §UI 1)에 따라 아이콘·이모지 없이 타이포와 헤어라인으로만 위계를 만든다.
 * 여닫는 것은 상태를 남기지 않는다 — 페이지를 옮기면 AdminLayout 이 닫는다.
 */

export default function HelpDrawer({
  topic,
  open,
  onClose,
}: {
  topic: HelpTopic | null;
  open: boolean;
  onClose: () => void;
}) {
  // Esc 로 닫는다. 드로어는 본문 위에 겹치므로 키보드 탈출구가 반드시 있어야 한다.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* 뒤 본문을 가리지 않고 클릭만 받는 투명 막 — 내용을 보며 읽을 수 있어야 한다 */}
      <div
        className="fixed inset-0 z-40 bg-gray-900/10"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        data-help-drawer
        role="dialog"
        aria-modal="false"
        aria-label={topic ? `${topic.title} 도움말` : "도움말"}
        className="fixed right-0 top-0 z-50 flex h-screen w-[26rem] flex-col border-l border-gray-200 bg-white shadow-[-1px_0_0_0_rgba(0,0,0,0.04)]"
      >
        <header className="flex shrink-0 items-baseline justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div>
            <div className="text-2xs font-medium tracking-wider text-gray-400">도움말</div>
            <h2 className="mt-0.5 text-sm font-semibold tracking-tight text-gray-900">
              {topic?.title ?? "화면 안내"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-xs text-gray-400 transition-colors hover:text-gray-900"
          >
            닫기
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {topic ? <HelpBody topic={topic} onNavigate={onClose} /> : <EmptyHelp />}
          <div className="mt-7 space-y-7 border-t border-gray-100 pt-6">
            <CommonControls />
            <Shortcuts />
          </div>
        </div>
      </aside>
    </>
  );
}

function EmptyHelp() {
  return (
    <p className="text-xs leading-relaxed text-gray-400">
      이 화면의 도움말은 아직 준비되지 않았습니다.
    </p>
  );
}

/**
 * 어느 화면에나 있는 컨트롤.
 *
 * 화면별 `controls` 와 섞지 않는다 — 25개 항목에 같은 설명을 되풀이하면 정작 그 화면의
 * 버튼이 묻힌다. 상단바·사이드바처럼 늘 같은 자리에 있는 것만 여기 모은다.
 */
const COMMON_CONTROLS: HelpControlItem[] = [
  {
    name: "조밀 · 보통 · 여유",
    description:
      "표의 행 높이를 바꿉니다. 글자 크기는 그대로고 여백만 달라집니다. 고른 값은 다음에 들어와도 유지됩니다.",
  },
  {
    name: "메뉴 접기",
    description:
      "왼쪽 메뉴를 접어 표를 넓게 씁니다. 접으면 상단에 ‘메뉴’ 버튼이 생기고, 눌러서 잠시 펼 수 있습니다.",
  },
  {
    name: "메뉴 고정",
    description: "접은 메뉴를 다시 왼쪽에 붙박이로 되돌립니다.",
  },
  {
    name: "필터 초기화",
    description:
      "걸어 둔 조회 조건을 한 번에 모두 풉니다. 조건은 필터 아래에 하나씩 표시되며, 그 하나만 눌러서 풀 수도 있습니다.",
  },
  {
    name: "사용 / 미사용",
    description:
      "기준정보 표의 마지막 칸입니다. 두 칸 중 진하게 표시된 쪽이 **지금 상태**이고, 누르면 누른 쪽 상태로 바뀝니다.",
  },
  {
    name: "? 도움말",
    description: "지금 보고 있는 화면의 안내를 엽니다. 키보드 `?` 로도 열립니다.",
  },
];

interface HelpControlItem {
  name: string;
  description: string;
}

function CommonControls() {
  return (
    <Section title="어느 화면에나 있는 것">
      <dl className="space-y-2.5">
        {COMMON_CONTROLS.map((control) => (
          <div key={control.name} className="flex gap-3">
            <dt className="w-24 shrink-0 text-xs font-medium text-gray-900">{control.name}</dt>
            <dd className="flex-1 text-xs leading-relaxed text-gray-500">{control.description}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

/**
 * 단축키 (설계원칙 P27 — 더 빠른 방법은 문맥 안에서 눈에 거슬리지 않게 알린다).
 *
 * 화면마다 다르지 않으므로 `help/content.ts` 에 넣지 않고 여기 고정으로 둔다.
 * 이 목록이 `?` 를 알려 주는 유일한 자리라 **모든 화면의 도움말 아래에 붙는다.**
 */
const SHORTCUTS: { keys: string; description: string }[] = [
  { keys: "?", description: "이 도움말을 열고 닫습니다" },
  { keys: "Esc", description: "도움말·확인창·드롭다운을 닫습니다" },
  { keys: "Tab", description: "표의 행과 편집할 수 있는 칸으로 차례로 옮겨 갑니다" },
  { keys: "Enter / Space", description: "고른 행을 열거나, 표의 칸을 편집 상태로 바꿉니다" },
  { keys: "↑ ↓", description: "드롭다운 후보 사이를 오갑니다" },
];

function Shortcuts() {
  return (
    <Section title="단축키">
      <dl className="space-y-2.5">
        {SHORTCUTS.map((shortcut) => (
          <div key={shortcut.keys} className="flex gap-3">
            <dt className="w-24 shrink-0 text-xs font-medium tabular-nums text-gray-900">
              {shortcut.keys}
            </dt>
            <dd className="flex-1 text-xs leading-relaxed text-gray-500">
              {shortcut.description}
            </dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function HelpBody({ topic, onNavigate }: { topic: HelpTopic; onNavigate: () => void }) {
  return (
    <div className="space-y-7">
      <Section title="이 화면에서 하는 일">
        <p className="text-xs leading-relaxed text-gray-600">{topic.summary}</p>
      </Section>

      {topic.screenshot && <Screenshot src={topic.screenshot} title={topic.title} />}

      {topic.steps.length > 0 && (
        <Section title="기본 사용법">
          <ol className="space-y-2">
            {topic.steps.map((step, index) => (
              <li key={step} className="flex gap-2.5 text-xs leading-relaxed text-gray-600">
                <span className="w-3 shrink-0 tabular-nums text-gray-400">{index + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {topic.controls.length > 0 && (
        <Section title="화면의 버튼">
          <dl className="space-y-2.5">
            {topic.controls.map((control) => (
              <ControlRow key={control.name} name={control.name}>
                {control.description}
              </ControlRow>
            ))}
          </dl>
        </Section>
      )}

      {topic.notes.length > 0 && (
        <Section title="알아 둘 것">
          <ul className="space-y-2">
            {topic.notes.map((note) => (
              <li key={note} className="flex gap-2.5 text-xs leading-relaxed text-gray-600">
                <span className="shrink-0 text-gray-300">·</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {topic.related.length > 0 && (
        <Section title="함께 보는 화면">
          <ul className="space-y-2">
            {topic.related.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  onClick={onNavigate}
                  className="text-xs font-medium text-gray-900 underline decoration-gray-300 underline-offset-4 transition-colors hover:decoration-gray-900"
                >
                  {link.label}
                </Link>
                <span className="ml-2 text-xs text-gray-400">{link.reason}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

/**
 * 화면 캡처. 파일은 `npm run capture-help` 가 만든다.
 *
 * 드로어 폭이 좁아 캡처가 작게 들어간다. 눌러서 원래 크기로 펴 볼 수 있게 한다.
 * 아직 안 뜬 화면(로컬에 데이터가 없어 건너뛴 경우)은 통째로 감춘다 — 깨진 이미지를 보이지 않는다.
 */
function Screenshot({ src, title }: { src: string | string[]; title: string }) {
  // **탭 화면은 한 장으로 반쪽이다.** 배차 등록은 기사·업체 두 탭이 서로 다른 표를 그리므로
  // 캡처도 둘이다. 각 장이 따로 실패할 수 있어 실패 여부도 장마다 센다.
  const shots = Array.isArray(src) ? src : [src];
  const [failed, setFailed] = useState<string[]>([]);
  const [zoomed, setZoomed] = useState<string | null>(null);
  const alive = shots.filter((shot) => !failed.includes(shot));
  if (alive.length === 0) return null;
  return (
    <>
      <Section title="화면">
        <div className="space-y-3">
          {alive.map((shot) => (
            <figure key={shot}>
              <button
                type="button"
                onClick={() => setZoomed(shot)}
                className="block w-full cursor-zoom-in"
                aria-label={`${title} 화면 캡처 크게 보기`}
              >
                <img
                  src={shot}
                  alt={`${title} 화면 캡처`}
                  loading="lazy"
                  className="w-full rounded border border-gray-200 transition-colors hover:border-gray-900"
                  onError={() => setFailed((prev) => [...prev, shot])}
                />
              </button>
            </figure>
          ))}
        </div>
        <p className="mt-1.5 text-2xs leading-relaxed text-gray-400">
          눌러서 크게 볼 수 있습니다. 실제 화면과 다르면 캡처가 오래된 것이니 화면의 표기를 기준으로
          하세요.
        </p>
      </Section>

      {zoomed && (
        <ScreenshotViewer src={zoomed} title={title} onClose={() => setZoomed(null)} />
      )}
    </>
  );
}

/**
 * 캡처 원본 뷰어. 드로어보다 위(z-60)에 전체 화면으로 덮는다.
 * 캡처는 폭 1440px 로 떠 두었으므로 화면이 좁으면 폭에 맞추고, 넓으면 원래 크기로 보여 준다.
 */
function ScreenshotViewer({
  src,
  title,
  onClose,
}: {
  src: string;
  title: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();     // 드로어까지 닫히지 않게 여기서 멈춘다
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} 화면 캡처`}
      onClick={onClose}
      className="fixed inset-0 z-[60] overflow-auto bg-gray-900/90 p-6"
    >
      <div className="mb-4 flex items-baseline justify-between gap-6">
        <h2 className="text-sm font-medium text-white">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-xs text-gray-300 transition-colors hover:text-white"
        >
          닫기
        </button>
      </div>
      {/* 이미지 자체를 눌렀을 때는 닫지 않는다 — 가로로 밀어 보는 중일 수 있다 */}
      <img
        src={src}
        alt={`${title} 화면 캡처`}
        onClick={(event) => event.stopPropagation()}
        className="mx-auto max-w-full rounded bg-white"
      />
    </div>
  );
}

/**
 * 버튼 설명 한 줄. **마우스를 올리면 화면의 그 조각을 짚는다** (담당자 요청 2026-09-15).
 *
 * 도움말이 버튼 이름을 말해 줘도 그게 화면 어디 있는지는 눈으로 찾아야 했다.
 * 드로어가 처음부터 비모달이고 뒤를 가리지 않는 것이 이걸 가능하게 한다.
 *
 * 짚을 것이 없으면(선택해야 뜨는 버튼, 모달 안의 것) **조용히 알린다** — 아무 일도
 * 안 일어나면 고장인지 원래 그런 건지 알 수 없다.
 *
 * 키보드로도 짚힌다(`onFocus`). 마우스를 못 쓰는 상황에서도 도움말은 같은 일을 해야 한다.
 */
function ControlRow({ name, children }: { name: string; children: React.ReactNode }) {
  const clear = useRef<(() => void) | null>(null);
  const [absent, setAbsent] = useState(false);

  // 드로어가 닫히거나 화면을 옮겨도 테두리가 남지 않게 한다
  useEffect(() => () => clear.current?.(), []);

  const enter = () => {
    clear.current?.();
    const found = findSpots(name).length > 0;
    setAbsent(!found);
    clear.current = found ? spotlight(name) : null;
  };
  const leave = () => {
    clear.current?.();
    clear.current = null;
    setAbsent(false);
  };

  return (
    <div
      className="flex gap-3 rounded-sm hover:bg-gray-50"
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
      tabIndex={0}
    >
      <dt className="w-24 shrink-0 text-xs font-medium text-gray-900">{name}</dt>
      <dd className="flex-1 text-xs leading-relaxed text-gray-500">
        {children}
        {absent && <span className="ml-1 text-gray-300">지금 화면에는 없습니다</span>}
      </dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2.5 text-2xs font-medium tracking-wider text-gray-400">{title}</h3>
      {children}
    </section>
  );
}
